import { useEffect, useRef, useState } from "react";
import { Gauge } from "lucide-react";
import api from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { BrandGrid, BrandLogo } from "@/components/BrandGrid";
import { Breadcrumb, StepHeader, TextTiles, EngineCards } from "@/components/ConfigSteps";
import ConfigResults from "@/components/ConfigResults";

const EMPTY = { brand: "", model: "", generation: null, vehicleId: "" };

function useList(url, params, enabled) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setData(null);
    api.get(url, { params }).then(({ data: d }) => { if (live) setData(d); });
    return () => { live = false; };
  }, [key, enabled]); // eslint-disable-line
  return data;
}

export default function Configurator() {
  const { openLead } = useUI();
  const [types, setTypes] = useState([]);
  const [vtype, setVtype] = useState("Voiture");
  const [sel, setSel] = useState(EMPTY);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/catalog/types").then(({ data }) => setTypes(data)); }, []);
  const brandsRaw = useList("/catalog/brands", { vtype }, true);
  const models = useList("/catalog/models", { vtype, brand: sel.brand }, Boolean(sel.brand));
  const gens = useList("/catalog/generations", { vtype, brand: sel.brand, model: sel.model }, Boolean(sel.model));
  const enginesRaw = useList("/catalog/engines", { vtype, brand: sel.brand, model: sel.model, generation: sel.generation }, sel.generation !== null);
  const brands = brandsRaw || [];
  const engines = enginesRaw || [];
  const loading = { brand: !brandsRaw, model: !models, year: !gens, engine: !enginesRaw };

  const step = !sel.brand ? "brand" : !sel.model ? "model" : sel.generation === null ? "year" : !sel.vehicleId ? "engine" : "result";
  const box = useRef(null);
  useEffect(() => {
    const el = box.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);
  const go = (patch) => { setSel((s) => ({ ...s, ...patch })); setQ(""); };
  const brandObj = brands.find((b) => b.name === sel.brand);
  const vehicle = engines.find((e) => e.id === sel.vehicleId);
  const name = [sel.brand, sel.model, sel.generation].filter(Boolean).join(" ");

  const back = { model: () => go(EMPTY), year: () => go({ model: "", generation: null }),
    engine: () => go({ generation: null, vehicleId: "" }), result: () => go({ vehicleId: "" }) }[step];
  const crumbs = [
    { label: vtype, onClick: () => go(EMPTY) },
    sel.brand && { label: sel.brand, onClick: () => go({ model: "", generation: null, vehicleId: "" }) },
    sel.model && { label: sel.model, onClick: () => go({ generation: null, vehicleId: "" }) },
    sel.generation !== null && { label: sel.generation || "Toutes années", onClick: () => go({ vehicleId: "" }) },
    vehicle && { label: vehicle.engine },
  ].filter(Boolean);

  const requestConfig = (stage) => openLead({
    request_type: "configurateur", brand: vehicle.brand, model: `${vehicle.model} ${vehicle.generation}`.trim(),
    engine: `${vehicle.engine}${vehicle.origin_hp ? ` ${vehicle.origin_hp} ch` : ""}`, service: stage, stage,
    vehicle_summary: `${vehicle.brand} ${vehicle.model} ${vehicle.generation} — ${vehicle.engine} ${vehicle.origin_hp || ""} ch (${stage})`,
    message: stage === "Demande de gain" ? "Demande de gain personnalisé (Stage 1 / Stage 2 / E85) depuis le configurateur." : `Configuration ${stage} demandée depuis le configurateur.`,
  });

  const titles = {
    brand: ["Sélectionnez votre marque", "Rechercher une marque..."],
    model: [`Sélectionnez votre modèle ${sel.brand}`, "Rechercher un modèle..."],
    year: [`Sélectionnez l'année de votre ${sel.brand} ${sel.model}`, "Rechercher une année..."],
    engine: [`Sélectionnez la configuration ${name}`, "Rechercher une configuration..."],
  };

  return (
    <div ref={box} className="bg-hp-dark border border-white/10 scroll-mt-24" data-testid="configurator">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-white/10 bg-black">
        <div className="flex items-center gap-3">
          <Gauge className="text-hp-yellow" size={22} />
          <div>
            <p className="label-eyebrow">Configurateur véhicule</p>
            <h3 className="font-display text-xl font-extrabold uppercase tracking-tight">Trouvez le potentiel de votre véhicule</h3>
          </div>
        </div>
        {types.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="config-types">
            {types.map((t) => (
              <button key={t} onClick={() => { setVtype(t); go(EMPTY); }} data-testid={`config-type-${t.toLowerCase()}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${t === vtype ? "bg-hp-yellow text-black border-hp-yellow" : "border-white/15 text-neutral-300 hover:border-hp-yellow"}`}>{t}</button>
            ))}
          </div>
        )}
      </div>

      <Breadcrumb items={crumbs} onBack={back} />

      <div className="p-6" data-testid={`config-step-${step}`}>
        {step !== "result" && <StepHeader title={titles[step][0]} search={q} setSearch={setQ} placeholder={titles[step][1]} />}
        {loading[step] && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="config-loading">
            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-16 bg-white/[0.04] animate-pulse" />)}
          </div>
        )}
        {step === "brand" && !loading.brand && <BrandGrid brands={brands} q={q} onPick={(b) => go({ brand: b })} />}
        {step === "model" && models && <TextTiles items={models} q={q} prefix="model" onPick={(m) => go({ model: m })} />}
        {step === "year" && gens && <TextTiles items={gens} q={q} prefix="year" onPick={(g) => go({ generation: g })} />}
        {step === "engine" && enginesRaw && <EngineCards engines={engines} q={q} onPick={(id) => go({ vehicleId: id })} />}
        {step === "result" && vehicle && (
          <div data-testid="config-results">
            <div className="flex items-center gap-4 mb-6" data-testid="selected-vehicle">
              {brandObj && <div className="bg-white h-14 w-20 flex items-center justify-center p-2 shrink-0"><BrandLogo brand={brandObj} size="h-9" /></div>}
              <div>
                <p className="font-display font-extrabold text-lg uppercase">{name}</p>
                <p className="text-sm text-neutral-400">{vehicle.engine} · {vehicle.fuel}{vehicle.origin_hp ? ` · ${vehicle.origin_hp} ch` : ""}</p>
              </div>
            </div>
            <ConfigResults vehicle={vehicle} onRequest={requestConfig} />
          </div>
        )}
      </div>
    </div>
  );
}
